import type { Volume } from '../types/content'

export const volumes: Volume[] = [
  {
    icon: '📘',
    number: 'Volume 1',
    title: 'Sprints, Speed & Acceleration',
    lessons: '30 Sessions',
    accent: '#0F2A52',
    subtitle:
      'Acceleration, sprint mechanics, maximum velocity, speed development, technical progressions and athlete assessment',
    problem:
      'Speed training requires more than telling athletes to sprint harder. This volume gives you structured sessions for developing acceleration, mechanics, maximum velocity and speed qualities progressively.',
    bullets: [
      'Starts and acceleration development, phase by phase',
      'Sprint mechanics and technical drills with clear coaching focus',
      'Maximum velocity work and how to place it inside the week',
      'Speed development progressions from foundation to advanced',
      'Corrections and athlete assessment for every sprint quality',
    ],
    result:
      'You stop searching for isolated sprint drills and start coaching speed through a clear progression.',
    popular: true,
  },
  {
    icon: '📗',
    number: 'Volume 2',
    title: 'Hurdles & Relays',
    lessons: '25 Sessions',
    accent: '#D99B12',
    subtitle:
      'Hurdle rhythm, clearance mechanics, lead and trail-leg development, starts, spacing, relay exchanges and team coordination',
    problem:
      'Hurdles and relays require rhythm, timing and technical precision. This volume gives you progressive sessions for teaching those skills without improvising the next step.',
    bullets: [
      'Hurdle rhythm and spacing progressions from walkover drills to race pace',
      'Clearance mechanics: lead leg, trail leg, posture and touchdown',
      'Hurdle starts and the approach to the first barrier',
      'Relay exchange technique, timing marks and zone work',
      'Team coordination sessions for reliable handoffs under pressure',
    ],
    result: 'Athletes develop cleaner hurdle rhythm and more reliable relay execution.',
  },
  {
    icon: '📙',
    number: 'Volume 3',
    title: 'Middle-Distance, Distance & Race Walking',
    lessons: '30 Sessions',
    accent: '#1E8E4E',
    subtitle:
      'Aerobic development, threshold, race-specific work, pacing, running economy, competition preparation and race walking technique',
    problem:
      'Endurance coaching requires control of intensity, volume and progression. This volume gives you structured sessions instead of random mileage or repeated hard intervals.',
    bullets: [
      'Aerobic development blocks with clear intensity targets',
      'Threshold and race-specific sessions by event distance',
      'Pacing education and running economy work',
      'Competition preparation weeks and sharpening sessions',
      'Race walking technique, judging rules and technical progressions',
    ],
    result: 'You can develop endurance athletes with a clearer purpose behind every session.',
  },
  {
    icon: '📕',
    number: 'Volume 4',
    title: 'Jumps',
    lessons: '25 Sessions',
    accent: '#C0392B',
    subtitle:
      'Long Jump, Triple Jump, High Jump and Pole Vault — approach development, takeoff mechanics, rhythm, landing, corrections and athlete assessment',
    problem:
      'Jumping performance depends on much more than simply taking more attempts. You receive structured progressions from fundamentals to competition-relevant execution.',
    bullets: [
      'Approach run development, run-up consistency and check marks',
      'Takeoff mechanics and rhythm for each jumping event',
      'Long Jump and Triple Jump phase work, landing and flight positions',
      'High Jump curve, penultimate step and bar clearance progressions',
      'Pole Vault fundamentals, plant, swing and safe technical build-up',
    ],
    result: 'You can identify where a jump is breaking down and know what to coach next.',
  },
  {
    icon: '📓',
    number: 'Volume 5',
    title: 'Throws',
    lessons: '25 Sessions',
    accent: '#6B4E9B',
    subtitle:
      'Shot Put, Discus, Javelin and Hammer — grip, stance, footwork, force transfer, rotation, blocking, release, safety and competition preparation',
    problem:
      'Throwing events involve complex sequencing and strict safety requirements. This volume gives you progressive systems for developing each event logically.',
    bullets: [
      'Grip, stance and footwork foundations for each throwing event',
      'Force transfer, rotation and blocking taught in sequence',
      'Release mechanics and implement-specific technical cues',
      'Safety protocols and session organization inside the throwing area',
      'Competition preparation and technical assessment for throwers',
    ],
    result:
      'Throwing sessions become safer, more organized and more technically purposeful.',
  },
  {
    icon: '📔',
    number: 'Volume 6',
    title: 'Complete Track & Field Coaching Vault',
    lessons: '15 Integrated Sessions',
    accent: '#2F6FB5',
    subtitle:
      'Mixed groups, large groups, preseason, in-season coaching, competition week, testing, athlete monitoring, combined events and season planning',
    problem:
      'Track & Field coaches rarely work with only one athlete and one event. This volume helps you manage the entire coaching environment.',
    bullets: [
      'Organizing mixed-event and large groups with limited equipment',
      'Preseason structure and in-season coaching flow',
      'Competition weeks: volume, intensity and readiness management',
      'Testing, monitoring and athlete readiness indicators',
      'Combined events, weekly planning and full season structure',
    ],
    result:
      'You move from running individual sessions to managing a complete Track & Field program.',
  },
  {
    icon: '📚',
    number: 'Volume 7',
    title: 'Applied Coaching Theory & Performance Foundations',
    lessons: 'Coaching Knowledge',
    accent: '#1E3A6B',
    subtitle:
      'Training science, energy systems, biomechanics, sprint theory, endurance physiology, jumping and throwing theory, motor learning, fatigue, recovery, periodization, youth development and competition psychology',
    problem:
      'The first six volumes show you what to coach and how to organize it. Volume 7 explains why it works.',
    bullets: [
      'Training science and energy systems applied to Track & Field',
      'Biomechanics, sprint theory, jumping and throwing principles',
      'Motor learning, fatigue management and recovery',
      'Periodization models and youth development pathways',
      'Competition psychology and coaching communication',
    ],
    result:
      'You gain the knowledge needed to adapt the system intelligently instead of blindly following sessions.',
  },
]
